import Link from "next/link";
import SEOConfig from "@/common/seo.config";
import { SITE } from "@/common/seo.config";
import { Footer, Header, SEO } from "@/components";
import {
  CtaBand,
  FaqList,
  LandingSection,
  PageHero,
  PrimaryButton,
  SecondaryButton,
  SectionHeader,
} from "@/components/main/landing/landing-ui";
import {
  buildBreadcrumbList,
  buildFaqPage,
  buildWebPage,
  pageSchemas,
} from "@/lib/schema";

const GROUPS = [
  {
    title: "Engagements and delivery",
    items: [
      {
        question: "How do projects typically start?",
        answer:
          "With a short discovery conversation or written brief. We clarify the workflow, systems, owners, and success criteria before proposing a pilot or build.",
      },
      {
        question: "How long until we see something working?",
        answer:
          "Focused pilots often show measurable results in weeks when data and operator access are available. Production timelines depend on integrations and review requirements.",
      },
      {
        question: "Do you only build, or do you also advise?",
        answer:
          "We advise as part of delivery. If AI is not the right first step, we will say so. We are not a pure strategy firm.",
      },
      {
        question: "How do you communicate during the project?",
        answer:
          "Weekly working sessions, a shared backlog, and demos against the success criteria agreed in discovery.",
      },
    ],
  },
  {
    title: "Use cases and pilots",
    items: [
      {
        question: "How do we choose the first use case?",
        answer:
          "Pick a painful, measurable workflow with sample data and an owner. Novelty without a path to production is usually a poor first bet.",
      },
      {
        question: "What does a successful pilot look like?",
        answer:
          "Real users, real data, measured outcomes, and a clear go or no-go. Demo-only success does not count.",
      },
      {
        question: "Can a pilot become production?",
        answer:
          "Often yes. We design pilots so successful pieces can harden rather than be thrown away, when the architecture allows it.",
      },
    ],
  },
  {
    title: "Technology and security",
    items: [
      {
        question: "Which models and clouds do you use?",
        answer:
          "We choose models and hosting based on your constraints: latency, cost, data residency, and existing vendor relationships. Architecture is documented as part of design.",
      },
      {
        question: "How do you handle sensitive data?",
        answer:
          "Data boundaries, access controls, and retention expectations are defined up front. We can work under NDA and align with your security review process.",
      },
      {
        question: "Will this replace our ERP or CRM?",
        answer:
          "Usually no. We integrate with systems of record so operators keep working in familiar tools while AI handles grounded assistance or automation.",
      },
      {
        question: "Do you fine-tune models?",
        answer:
          "When it is justified. Many programs start with retrieval and tooling first. Fine-tuning is used when evaluation shows clear benefit.",
      },
    ],
  },
  {
    title: "Fit and commercial",
    items: [
      {
        question: "What makes a strong fit for Nivalabs?",
        answer:
          "A concrete operational problem, available systems and sample data, and an owner who will run the system after launch.",
      },
      {
        question: "Can we start with a small pilot?",
        answer:
          "Yes. Many clients begin with MVP or PoC work to prove value and integration risk before funding full production.",
      },
      {
        question: "Where are you based?",
        answer:
          "Nivalabs is a PySquad company with global delivery. We work with teams across time zones and align communication to your stakeholders.",
      },
      {
        question: "Do you work under NDA?",
        answer:
          "Yes. Mutual NDAs are common before detailed data or architecture discussions.",
      },
    ],
  },
];

export default function FaqPage() {
  const allFaqs = GROUPS.flatMap((group) => group.items);
  const structuredData = pageSchemas([
    buildWebPage({
      path: "/faq",
      name: SEOConfig.faq.pageTitle,
      description: SEOConfig.faq.pageDescription,
    }),
    buildFaqPage(allFaqs, { id: `${SITE.siteUrl}/faq#faq` }),
    buildBreadcrumbList([
      { name: "Home", path: "/" },
      { name: "FAQ", path: "/faq" },
    ]),
  ]);

  return (
    <>
      <SEO {...SEOConfig.faq} structuredData={structuredData} />
      <Header />
      <main>
        <PageHero
          title="Frequently asked questions"
          lead="Straight answers for product, operations, and engineering leaders evaluating Nivalabs."
          primaryCta={
            <PrimaryButton href="/contact">Ask us directly</PrimaryButton>
          }
          secondaryCta={
            <SecondaryButton href="/how-we-work">How we work</SecondaryButton>
          }
        />

        {GROUPS.map((group, index) => (
          <LandingSection key={group.title} muted={index % 2 === 1}>
            <SectionHeader title={group.title} />
            <FaqList items={group.items} />
          </LandingSection>
        ))}

        <LandingSection>
          <SectionHeader
            title="Still looking for something specific?"
            lead="Browse use cases and delivery detail, or send a short note about your workflow."
          />
          <div className="mt-8 flex flex-wrap gap-3">
            <Link href="/use-cases" className="landing-btn-secondary">
              Use cases
            </Link>
            <Link href="/how-we-work" className="landing-btn-secondary">
              How we work
            </Link>
            <Link href="/solutions" className="landing-btn-secondary">
              Solutions
            </Link>
            <Link href="/privacy" className="landing-btn-secondary">
              Privacy
            </Link>
          </div>
        </LandingSection>

        <CtaBand
          title="Did not find your question?"
          description="Write to us with the workflow and systems involved. We will respond with clear next steps."
          secondaryText="Contact form"
          secondaryHref="/contact"
        />
      </main>
      <Footer />
    </>
  );
}
